/** A ticket id is local to its customer schema. UI keys must retain both parts. */
export interface TicketReference { customerId: string; ticketId: string }

export function ticketReference(id: string | number, customerId?: string | number | null): TicketReference | null {
  const parts = String(id).split(':');
  const customer = parts.length === 2 ? parts[0] : String(customerId ?? '');
  const ticket = parts.length === 2 ? parts[1] : parts[0];
  if (!/^[1-9]\d*$/.test(customer) || !/^[1-9]\d*$/.test(ticket)) return null;
  if (parts.length === 2 && customerId != null && String(customerId) !== customer) throw new Error('Widersprüchlicher Kundenkontext');
  return { customerId: customer, ticketId: ticket };
}

export function ticketKey(id: string | number, customerId?: string | number | null): string {
  const ref = ticketReference(id, customerId);
  return ref ? `${ref.customerId}:${ref.ticketId}` : String(id);
}

export function normalizeConsoleTicket<T extends { id: string | number; customer_id?: string | number | null; tenant_id?: string | number | null }>(ticket: T): T & { id: string } {
  return { ...ticket, id: ticketKey(ticket.id, ticket.customer_id ?? ticket.tenant_id) };
}

/** Decode request-local references. Never consult a mutable global customer filter. */
export function scopeTicketRequest(config: any): void {
  let customerId: string | undefined;
  const decode = (value: unknown): unknown => {
    if (typeof value !== 'string' || !/^[1-9]\d*:[1-9]\d*$/.test(value)) return value;
    const ref = ticketReference(value)!;
    if (customerId && customerId !== ref.customerId) throw new Error('Tickets verschiedener Kunden können nicht gemeinsam bearbeitet werden');
    customerId = ref.customerId;
    return ref.ticketId;
  };
  config.url = String(config.url ?? '').replace(/(^|\/)([1-9]\d*(?::|%3A)[1-9]\d*)(?=\/|\?|$)/gi,
    (_, prefix: string, key: string) => prefix + decode(decodeURIComponent(key)));
  const fields = ['ticket_id', 'ticketId', 'target_ticket_id', 'source_ticket_ids', 'incident_ids'];
  for (const data of [config.params, config.data]) {
    if (!data || typeof data !== 'object') continue;
    for (const field of fields) {
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        const value = data.get(field); if (typeof value === 'string') data.set(field, String(decode(value)));
      } else if (field in data) {
        data[field] = Array.isArray(data[field]) ? data[field].map(decode) : decode(data[field]);
      }
    }
  }
  if (customerId) {
    const headers = config.headers;
    const existing = headers?.get?.('X-Customer-Id') ?? headers?.['X-Customer-Id'];
    if (existing != null && String(existing) !== customerId) throw new Error('Widersprüchlicher Kundenkontext');
    if (headers?.set) headers.set('X-Customer-Id', customerId);
    else config.headers = { ...headers, 'X-Customer-Id': customerId };
  }
}
